import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {HTMLElement | null} Element that had focus before the drawer opened. */
  #returnFocusTo = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Legacy events dispatched by custom buy boxes (for example membership-buy-box).
    document.addEventListener('cart:open', this.#handleLegacyOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpened);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:open', this.#handleLegacyOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpened);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClosed);
    document.body.classList.remove('mc-cart-drawer-open');
  }

  /**
   * @param {Event & { detail?: { data?: { skipOpen?: boolean, source?: string } } }} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    const data = event.detail?.data;
    // Changes made inside the cart itself, or refreshes that did not add anything, never pop the drawer open.
    if (data?.skipOpen) return;
    if (data?.source === 'cart-items-component') return;
    this.#rememberFocus();
    this.showDialog();
  };

  #handleLegacyOpen = () => {
    this.open();
  };

  #rememberFocus() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog?.open) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) this.#returnFocusTo = active;
  }

  #handleOpened = () => {
    document.body.classList.add('mc-cart-drawer-open');
    this.addEventListener('keydown', this.#trapTab);
    document.addEventListener(CartAddEvent.eventName, this.#restoreFocusInside);
    this.#restoreFocusInside();
  };

  /** Focusable elements inside the open drawer, in DOM order. */
  #focusables() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    return /** @type {HTMLElement[]} */ (Array.from(dialog.querySelectorAll(selector))).filter(
      (element) => !element.hidden && element.getClientRects().length > 0 && !element.closest('[inert], [hidden]')
    );
  }

  /**
   * Keeps Tab and Shift+Tab cycling inside the drawer.
   * @param {KeyboardEvent} event
   */
  #trapTab = (event) => {
    if (event.key !== 'Tab') return;
    const items = this.#focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const { dialog } = /** @type {Refs} */ (this.refs);
    const outside = !active || !dialog?.contains(active);
    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first?.focus();
    }
  };

  /** After the drawer opens or its content is re-rendered, make sure focus is still inside it. */
  #restoreFocusInside = () => {
    window.setTimeout(() => {
      const { dialog } = /** @type {Refs} */ (this.refs);
      if (!dialog?.open) return;
      const active = document.activeElement;
      if (active && active !== document.body && dialog.contains(active)) return;
      const close = /** @type {HTMLElement | null} */ (dialog.querySelector('.cart-drawer__close-button'));
      close?.focus({ preventScroll: true });
    }, 400);
  };

  #handleClosed = () => {
    document.body.classList.remove('mc-cart-drawer-open');
    this.removeEventListener('keydown', this.#trapTab);
    document.removeEventListener(CartAddEvent.eventName, this.#restoreFocusInside);
    const target = this.#returnFocusTo;
    this.#returnFocusTo = null;
    if (target && target.isConnected) {
      target.focus({ preventScroll: true });
    } else {
      this.querySelector('[data-testid="cart-drawer-trigger"]')?.focus({ preventScroll: true });
    }
  };

  open() {
    this.#rememberFocus();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
